import { SiteHeader } from "@/components/site/SiteHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import { createFileRoute, Link } from "@tanstack/react-router";
import { chooseLanguage } from "@/i18n";
import { chargeDisclosure } from "@/lib/booking-display";
import { useCurrency, useMoney } from "@/lib/currency";
import { queryOptions, useSuspenseQuery, useMutation } from "@tanstack/react-query";
import { lazy, Suspense, useState, useEffect } from "react";
import { trackRecoveryIntentClient } from "@/hooks/use-recovery-intent";
import { z } from "zod";
import { useTranslation } from "react-i18next";
import i18n from "@/i18n";
import { normalizeLang } from "@/lib/lang";

import { getCart } from "@/lib/cart.functions";

// PERF: el SDK de Stripe (~90 kB) se descarga sólo al llegar al paso de pago.
const StripeEmbeddedPanel = lazy(() => import("@/components/booking/StripeEmbeddedPanel"));
import { startBookingCheckout } from "@/lib/booking.functions";
import { getStripeEnvironment, isStripeConfigured, isTestMode } from "@/lib/stripe-browser";
import { PaymentBrandNotice } from "@/components/booking/PaymentBrandNotice";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const cartQuery = queryOptions({
  queryKey: ["cart"],
  queryFn: () => getCart(),
  staleTime: 0,
});

export const Route = createFileRoute("/checkout")({
  head: () => ({
    meta: [
      { title: "Pago de tu reserva — Mostrando México" },
      { name: "description", content: "Confirma tus datos y revisa el importe de tu reserva en Mostrando México." },
      { property: "og:title", content: "Pago de tu reserva — Mostrando México" },
      { property: "og:description", content: "Revisa tu reserva y completa el pago seguro." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  loader: ({ context }) => context.queryClient.ensureQueryData(cartQuery),
  component: CheckoutPage,
});

const CustomerForm = z.object({
  firstName: z.string().trim().min(1, "required").max(80),
  lastName: z.string().trim().min(1, "required").max(80),
  email: z.string().trim().email("invalid_email").max(255),
  phone: z.string().trim().max(30).optional().default(""),
  country: z.string().trim().min(2).max(2).default("MX"),
  language: z.string().trim().min(2).max(5).default("es"),
});

const COUNTRIES: Array<{ code: string; name: string }> = [
  { code: "MX", name: "México" },
  { code: "US", name: "Estados Unidos" },
  { code: "CA", name: "Canadá" },
  { code: "ES", name: "España" },
  { code: "AR", name: "Argentina" },
  { code: "CO", name: "Colombia" },
  { code: "CL", name: "Chile" },
  { code: "PE", name: "Perú" },
  { code: "BR", name: "Brasil" },
  { code: "GB", name: "Reino Unido" },
  { code: "DE", name: "Alemania" },
  { code: "FR", name: "Francia" },
  { code: "IT", name: "Italia" },
  { code: "NL", name: "Países Bajos" },
  { code: "JP", name: "Japón" },
  { code: "AU", name: "Australia" },
];

const LANGUAGES: Array<{ code: string; name: string }> = [
  { code: "es", name: "Español" },
  { code: "en", name: "English" },
  { code: "fr", name: "Français" },
  { code: "de", name: "Deutsch" },
  { code: "it", name: "Italiano" },
  { code: "pt", name: "Português" },
];

function CheckoutPage() {
  const { t } = useTranslation();
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <header className="border-b">
        <div className="mx-auto max-w-5xl px-6 py-4">
          <Link to="/cart" className="text-sm text-muted-foreground hover:text-foreground">
            {t("bookingFlow.checkout.back")}
          </Link>
        </div>
      </header>
      <main className="mx-auto max-w-5xl px-6 py-8">
        <h1 className="mb-6 text-2xl font-semibold">{t("bookingFlow.checkout.title")}</h1>
        {isTestMode() && (
          <div className="mb-6 rounded-md border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-700 dark:text-amber-300">
            {t("bookingFlow.checkout.testMode")}
          </div>
        )}
        <Suspense fallback={<Skeleton className="h-64 w-full" />}>
          <CheckoutBody />
        </Suspense>
      </main>
      <SiteFooter />
    </div>
  );
}

function paxOf(item: { pricingCategoryBookings?: { quantity: number }[] }): number | undefined {
  const n = (item.pricingCategoryBookings ?? []).reduce((s, c) => s + Number(c.quantity ?? 0), 0);
  return n > 0 ? n : undefined;
}

function recordCheckoutProblem(
  items: {
    productId: string;
    title: string;
    unitTotal?: number | null;
    currency?: string | null;
  }[],
  message: string,
) {
  for (const item of items) {
    trackRecoveryIntentClient({
      tourSlug: String(item.productId),
      tourTitle: item.title,
      stage: "checkout_error",
      amount: item.unitTotal ?? undefined,
      currency: item.currency ?? undefined,
      error: message,
    });
  }
}

function CheckoutBody() {
  const money = useMoney();
  const { t, i18n: activeI18n } = useTranslation();
  const { currency: displayCurrency, rates } = useCurrency();
  // Atribución comercial: si el viajero llegó desde una recomendación de Maya
  // en las últimas 12 horas, registramos que el checkout viene de Maya.
  useEffect(() => {
    try {
      const raw = localStorage.getItem("mxmx_maya_attribution");
      if (!raw) return;
      const ref = JSON.parse(raw) as { slug?: string; at?: number };
      if (!ref?.at || Date.now() - ref.at > 12 * 3600_000) return;
      void import("@/lib/maya-tracking.functions").then(({ logMayaEvent }) =>
        logMayaEvent({
          data: {
            event_type: "maya_checkout_started",
            metadata: { slug: ref.slug ?? null },
          },
        }),
      );
    } catch {}
  }, []);
  const { data: cart } = useSuspenseQuery(cartQuery);
  // Recuperación de ventas: registramos el intento de compra por tour.
  useEffect(() => {
    for (const item of cart.items) {
      trackRecoveryIntentClient({
        tourSlug: String(item.productId),
        tourTitle: item.title,
        stage: "checkout_started",
        amount: item.unitTotal ?? undefined,
        currency: item.currency ?? undefined,
        travelers: paxOf(item),
        activityDate: item.date,
      });
    }
  }, [cart.items]);
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    country: "MX",
    // Seeded from the active interface language; once the traveller picks one
    // explicitly it is never overwritten (state is only initialised here).
    language: normalizeLang(i18n.language),
  });
  useEffect(() => { setForm(current => ({ ...current, language: normalizeLang(activeI18n.language) })); }, [activeI18n.language]);
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof typeof form, string>>>({});
  const [clientSecret, setClientSecret] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: (customer: z.infer<typeof CustomerForm>) =>
      startBookingCheckout({ data: { customer, environment: getStripeEnvironment() } }),
    onSuccess: (res) => {
      if (res.ok && res.clientSecret) setClientSecret(res.clientSecret);
      else
        recordCheckoutProblem(
          cart.items,
          (res as any)?.error ?? t("bookingFlow.checkout.startError"),
        );
    },
    onError: (err: unknown) =>
      recordCheckoutProblem(cart.items, err instanceof Error ? err.message : String(err)),
  });

  if (!cart.items.length && !clientSecret) {
    return (
      <div className="rounded-2xl border bg-card p-8 text-center sm:p-10">
        <p className="text-muted-foreground">{t("bookingFlow.checkout.empty")}</p>
        <Link to="/" className="mt-4 inline-block text-sm font-medium underline">
          {t("bookingFlow.checkout.seeTours")}
        </Link>
      </div>
    );
  }

  const total = cart.items.reduce((s, i) => s + (i.unitTotal ?? 0), 0);
  const currency = cart.items[0]?.currency ?? "USD";

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!isStripeConfigured()) {
      setFieldErrors({ email: t("bookingFlow.checkout.notConfigured") });
      return;
    }
    const parsed = CustomerForm.safeParse(form);
    if (!parsed.success) {
      const errs: Partial<Record<keyof typeof form, string>> = {};
      for (const i of parsed.error.issues) {
        const k = i.path[0] as keyof typeof form;
        errs[k] =
          i.message === "invalid_email"
            ? t("bookingFlow.checkout.invalidEmail")
            : t("bookingFlow.checkout.required");
      }
      setFieldErrors(errs);
      return;
    }
    setFieldErrors({});
    mut.mutate(parsed.data);
  }

  // Once we have a clientSecret, render Embedded Checkout full width.
  if (clientSecret) {
    return (
      <Suspense fallback={<Skeleton className="h-64 w-full" />}>
        <StripeEmbeddedPanel clientSecret={clientSecret} />
      </Suspense>
    );
  }

  return (
    <div className="grid gap-6 md:grid-cols-[1fr_340px] lg:gap-8">
      <form
        className="space-y-5 rounded-2xl border bg-card p-5 shadow-card sm:p-7"
        onSubmit={submit}
      >
        <h2 className="text-lg font-semibold tracking-tight">{t("bookingFlow.checkout.holder")}</h2>
        <div className="grid gap-4 sm:grid-cols-2 sm:gap-5">
          <Field
            label={t("bookingFlow.checkout.firstName")}
            value={form.firstName}
            onChange={(v) => setForm({ ...form, firstName: v })}
            error={fieldErrors.firstName}
          />
          <Field
            label={t("bookingFlow.checkout.lastName")}
            value={form.lastName}
            onChange={(v) => setForm({ ...form, lastName: v })}
            error={fieldErrors.lastName}
          />
        </div>
        <Field
          label={t("bookingFlow.checkout.email")}
          type="email"
          value={form.email}
          onChange={(v) => setForm({ ...form, email: v })}
          error={fieldErrors.email}
        />
        <Field
          label={t("bookingFlow.checkout.phone")}
          value={form.phone}
          onChange={(v) => setForm({ ...form, phone: v })}
          error={fieldErrors.phone}
        />
        <div className="grid gap-4 sm:grid-cols-2 sm:gap-5">
          <div className="space-y-1">
            <Label>{t("bookingFlow.checkout.country")}</Label>
            <Select value={form.country} onValueChange={(v) => setForm({ ...form, country: v })}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {COUNTRIES.map((c) => (
                  <SelectItem key={c.code} value={c.code}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label>{t("bookingFlow.checkout.language")}</Label>
            <Select
              value={form.language}
              onValueChange={(v) => void chooseLanguage(normalizeLang(v))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {LANGUAGES.map((l) => (
                  <SelectItem key={l.code} value={l.code}>
                    {l.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {mut.data && !mut.data.ok && (
          <p className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
            {mut.data.error}
          </p>
        )}

        <Button type="submit" className="h-12 w-full text-base" disabled={mut.isPending}>
          {mut.isPending
            ? t("bookingFlow.checkout.preparing")
            : `${t("bookingFlow.checkout.continueLabel")} — ${money(total, currency)}`}
        </Button>
        <p className="text-xs text-muted-foreground">{t("bookingFlow.checkout.payNote")}</p>
        <p role="status" className="text-sm font-semibold">{chargeDisclosure(total, currency, displayCurrency, activeI18n.language, rates)}</p>
        <PaymentBrandNotice />
      </form>

      <aside className="h-fit space-y-3 rounded-2xl border bg-card p-5 shadow-card sm:p-6 md:sticky md:top-24">
        <h2 className="mm-eyebrow text-muted-foreground">{t("bookingFlow.checkout.summary")}</h2>
        {cart.items.map((i) => {
          const pax = i.pricingCategoryBookings.reduce((s, b) => s + b.quantity, 0);
          return (
            <div key={i.id} className="text-sm">
              <div className="font-medium">{i.title}</div>
              <div className="text-muted-foreground">
                {i.date} · {pax} pax · {money(i.unitTotal ?? 0, i.currency)}
              </div>
            </div>
          );
        })}
        <hr />
        <div className="flex justify-between text-sm font-semibold">
          <span>{t("bookingFlow.checkout.total")}</span>
          <span>{money(total, currency)}</span>
        </div>
      </aside>
    </div>
  );
}

function Field(props: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  error?: string;
}) {
  return (
    <div className="space-y-1.5">
      <Label>{props.label}</Label>
      <Input
        type={props.type ?? "text"}
        value={props.value}
        onChange={(e) => props.onChange(e.target.value)}
      />
      {props.error && <p className="text-xs text-destructive">{props.error}</p>}
    </div>
  );
}
